import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import type { W_BillingCode_FindForJob_VM_Single } from 'ab-api/web/billingCode/findForJob';
import type { W_FieldReport_FindWorkSummary_VM_Row as WorkSummaryVM } from 'ab-api/web/fieldReport/findWorkSummary/viewModel';
import type { W_WorkOrderReport_FindProductionData_VM } from 'ab-api/web/workOrderReport/findProductionData';

import type { BillingCodeVM } from 'ab-viewModels/billingCode.viewModel';
import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type WorkSummaryStatusVM from 'ab-viewModels/workOrder/workSummaryStatus.viewModel';
import type SiblingSubjobVM from 'ab-viewModels/workRequest/siblingSubjob.viewModel';

import type WorkSummaryDetailsAssignBillingCodeRM from 'ab-requestModels/workSummaryDetails/assignBillingCode.requestModel';

import type { WorkSummaryUpdatedBillingCodesVM } from 'ab-socketModels/viewModels/workOrder/workSummaryUpdatedBillingCodes.viewModel';

import * as ArrayUtils from '@acceligentllc/shared/utils/array';
import { isAllowed } from '@acceligentllc/shared/utils/auth';

import { WorkSummaryStatus as WorkSummaryStatusEnum } from '@acceligentllc/shared/enums/workSummaryStatus';

import type { RootState } from 'af-reducers';

import { bemBlock } from 'af-utils/bem.util';
import * as FieldReportUtil from 'af-utils/fieldReport.util';
import socket from 'af-utils/socket.util';
import * as UserUtils from 'af-utils/user.util';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';
import PagePermissions from 'ab-enums/pagePermissions.enum';
import SocketEvent from 'ab-enums/socketEvent.enum';
import WorkSummaryBillingType from 'ab-enums/workSummaryBillingType.enum';

import DisabledFeatures from 'ab-common/environment/disabledFeatures';

import CLIENT from 'af-routes/client';

import * as BillingCodeActions from 'af-actions/billingCode';
import * as FieldReportActions from 'af-actions/fieldReport';
import * as WorkOrderActions from 'af-actions/workOrder';
import * as WorkRequestActions from 'af-actions/workRequests';
import * as WorkSummaryDetailsActions from 'af-actions/workSummaryDetails';

import WorkSummaryBillableWork from './BillableAndNonBillableWork/BillableWork';
import WorkSummaryMaterialCost from './BillableAndNonBillableWork/MaterialCost';
import MaterialNonCost from './BillableAndNonBillableWork/MaterialNonCost';
import NonBillableWork from './BillableAndNonBillableWork/NonBillableWork';
import WorkSummaryDetails from './Details';
import { resolveHighlightGroupKey } from './helpers';
import { MaterialCostSourceFilterContext, type IMaterialCostSourceFilterContext } from './MaterialCostSourceFilterContext';
import ProductionData from './ProductionData';
import WorkSummaryStatus from './Status';

const MATERIAL_COST_SOURCE_FILTERS_INITIAL_VALUE: Record<MaterialCostSource, boolean> = {
	[MaterialCostSource.COMPANY]: true,
	[MaterialCostSource.COMPANY_MATERIAL]: true,
	[MaterialCostSource.JOB]: true,
	[MaterialCostSource.JOB_MATERIAL]: true,
};

interface OwnProps {
	jobId: number;
	fieldReportId: number;
	workOrderId: number;
	invalidateWorkSummaryStatus: () => Promise<void>;
	workSummaryStatus: WorkSummaryStatusVM;
	workOrderCode: string;
	canCompleteWorkSummary: boolean;
	hasPermissionToManageWS: boolean;
	areFRsReadOnly: boolean;
	pathName: string;
	isAbleToReject: boolean;
	belongsToProject: boolean;
	workOrderIsInProgess: boolean;
	timeZone: Nullable<string>;
}

type Props = ConnectedProps<typeof connector> & OwnProps;

interface WorkSummariesPartitioned {
	billableWorkSummaries: Nullable<WorkSummaryVM[]>;
	nonBillableWorkSummaries: Nullable<WorkSummaryVM[]>;
	billableMaterialCosts: Nullable<WorkSummaryVM[]>;
	nonBillableMaterialCosts: Nullable<WorkSummaryVM[]>;
}

function definitionFieldSort(df1: { index: number; }, df2: { index: number; }) {
	return df1.index - df2.index;
}

function definitionFieldValue(df: { value: string; }) {
	return df.value;
}

function workSummarySortKey(ws: WorkSummaryVM) {
	return JSON.stringify([
		ws.billableWorkId,
		ws.type,
		[...ws.definitionFields].sort(definitionFieldSort).map(definitionFieldValue),
	]);
}

const _sortWorkSummaries = (_wsa: WorkSummaryVM, _wsb: WorkSummaryVM) => {
	const _wsaGroupKey = workSummarySortKey(_wsa);
	const _wsbGroupKey = workSummarySortKey(_wsb);

	return _wsaGroupKey.localeCompare(_wsbGroupKey);
};

const _sortWorkSummaryDetails = (_wsda: WorkSummaryDetailVM, _wsdb: WorkSummaryDetailVM) => {
	const _wsaGroupKey = resolveHighlightGroupKey(_wsda);
	const _wsbGroupKey = resolveHighlightGroupKey(_wsdb);

	return _wsaGroupKey.localeCompare(_wsbGroupKey);
};

const _isEveryWorkSummaryBilled = (_ws: WorkSummaryVM) => (
	(_ws.billingType === WorkSummaryBillingType.BILLABLE_WORK && !!_ws.billingCodeId)
	|| (_ws.billingType === WorkSummaryBillingType.MATERIAL_COST && !!_ws.materialId)
);

const WorkSummary: React.FC<Props> = (props) => {
	const {
		findBillingCodesForJobId,
		findWorkSummaryDetails,
		jobId,
		fieldReportId,
		findWorkSummary,
		updateBillingStatuses,
		assignBillingCode,
		workOrderId,
		invalidateWorkSummaryStatus,
		workSummaryStatus,
		editWorkSummaryStatus,
		canCompleteWorkSummary,
		hasPermissionToManageWS,
		areFRsReadOnly,
		workOrderCode,
		pathName,
		checkIfBillableWorkExists,
		isAbleToReject,
		companyData,
		organizationData,
		allowToMarkAllAsNonBillable,
		bulkUpdateBillableStatusForFieldReport,
		isPlatformAdmin,
		resetJobWorkSummary,
		findSiblingSubjobs,
		belongsToProject,
		reallocateToSubJob,
		reallocateAllBillableToSubJob,
		reallocateAllNonBillableToSubJob,
		workOrderIsInProgess,
		bulkUpdateDetailBillingStatus,
		areMaterialsEnabled,
		timeZone,
		isAllowedToViewProdData,
		findProductionData,
	} = props;

	const [billingCodesByJob, setBillingCodesByJob] = React.useState<Nullable<Record<string, W_BillingCode_FindForJob_VM_Single[]>>>(null);
	const [workSummaryDetails, setWorkSummaryDetails] = React.useState<Nullable<WorkSummaryDetailVM[]>>(null);
	const [workSummaries, setWorkSummaries] = React.useState<Nullable<WorkSummaryVM[]>>(null);
	const [subJobs, setSubJobs] = React.useState<SiblingSubjobVM[]>([]);
	const [materialCostSourceFilters, setMaterialCostSourceFilters] = React.useState(MATERIAL_COST_SOURCE_FILTERS_INITIAL_VALUE);
	const [productionData, setProductionData] = React.useState<Nullable<W_WorkOrderReport_FindProductionData_VM>>(null);

	const [highlightedGroup, setHighlightedGroup] = React.useState<Nullable<string>>(null);
	const [highlightedGroupKey, setHighlightedGroupKey] = React.useState<Nullable<string>>(null);

	const [noWorkSummaryMessage, setNoWorkSummaryMessage] = React.useState<Nullable<string>>(null);

	const isWorkSummaryReadOnly = (workSummaryStatus.status === WorkSummaryStatusEnum.COMPLETED) || areFRsReadOnly;

	const fetchAndInitializeData = React.useCallback(
		async () => {
			const [
				fetchedBillingCodes,
				fetchedWorkSummaryDetails,
				fetchedWorkSummaries,
				fetchedSubJobs,
				fetchedProductionData,
			] = await Promise.all([
				findBillingCodesForJobId(jobId),
				findWorkSummaryDetails(fieldReportId),
				findWorkSummary(fieldReportId),
				findSiblingSubjobs(jobId),
				isAllowedToViewProdData ? findProductionData(workOrderId) : Promise.resolve(null),
			]);

			if (fetchedWorkSummaryDetails.length === 0) {
				const billableWorkExists = await checkIfBillableWorkExists(fieldReportId);
				const emptyWorkSummaryMessage = billableWorkExists
					? 'Fill in the Report Type to generate Work Summary.'
					: 'No Report Types are generating Work Summary.';
				setNoWorkSummaryMessage(emptyWorkSummaryMessage);
			}

			fetchedWorkSummaryDetails.sort(_sortWorkSummaryDetails);
			fetchedWorkSummaries.sort(_sortWorkSummaries);

			setWorkSummaryDetails(fetchedWorkSummaryDetails);
			setBillingCodesByJob(ArrayUtils.groupBy(fetchedBillingCodes, (_bc) => _bc.workRequestId));
			setWorkSummaries(fetchedWorkSummaries);
			setSubJobs(fetchedSubJobs);
			setProductionData(fetchedProductionData);
		}, [
		checkIfBillableWorkExists,
		fieldReportId,
		findBillingCodesForJobId,
		findProductionData,
		findSiblingSubjobs,
		findWorkSummary,
		findWorkSummaryDetails,
		jobId,
		workOrderId,
		isAllowedToViewProdData,
	]);

	const loadWorkSummaries = React.useCallback(async () => {
		const [fetchedWorkSummaries, fetchedWorkSummaryDetails, fetchedProductionData] = await Promise.all([
			findWorkSummary(fieldReportId),
			findWorkSummaryDetails(fieldReportId),
			isAllowedToViewProdData ? findProductionData(workOrderId) : Promise.resolve(null),
		]);

		fetchedWorkSummaries.sort(_sortWorkSummaries);
		fetchedWorkSummaryDetails.sort(_sortWorkSummaryDetails);

		setWorkSummaries(fetchedWorkSummaries);
		setWorkSummaryDetails(fetchedWorkSummaryDetails);
		setProductionData(fetchedProductionData);
	}, [fieldReportId, findProductionData, findWorkSummary, findWorkSummaryDetails, isAllowedToViewProdData, workOrderId]);

	React.useEffect(() => {
		FieldReportUtil.joinFieldReportWorkSummary(fieldReportId);

		socket.connection?.subscribe(SocketEvent.V2.BE.FIELD_REPORT.SYNC_WORK_SUMMARY, loadWorkSummaries);

		return () => {
			FieldReportUtil.leaveFieldReportWorkSummary(fieldReportId);

			socket.connection?.unsubscribe(SocketEvent.V2.BE.FIELD_REPORT.SYNC_WORK_SUMMARY);
		};
	}, [fieldReportId, loadWorkSummaries]);

	const workSummaryDetailsByGroupKey = React.useMemo(() => {
		return workSummaryDetails ? ArrayUtils.groupBy<WorkSummaryDetailVM>(workSummaryDetails, resolveHighlightGroupKey) : {};
	}, [workSummaryDetails]);

	const onBillingStatusToggle = React.useCallback(async (workSummaryDetail: WorkSummaryDetailVM) => {
		const { id, isBillable } = workSummaryDetail;

		if (isWorkSummaryReadOnly) {
			return;
		}

		await updateBillingStatuses({ fieldReportId, workSummaryDetails: [{ isBillable: !isBillable, id }] });
		await invalidateWorkSummaryStatus();
	}, [fieldReportId, invalidateWorkSummaryStatus, isWorkSummaryReadOnly, updateBillingStatuses]);

	const handleBulkUpdateBillableStatusForFieldReport = React.useCallback(async (billingType: WorkSummaryBillingType, isBillable: boolean) => {
		await bulkUpdateBillableStatusForFieldReport({ fieldReportId, billingType, isBillable });
		await invalidateWorkSummaryStatus();
	}, [bulkUpdateBillableStatusForFieldReport, fieldReportId, invalidateWorkSummaryStatus]);

	const onBulkUpdateDetailStatus = React.useCallback(async (workSummaryDetailIds: number[], isBillable: boolean) => {
		if (!workSummaryDetailIds) {
			return;
		}

		await bulkUpdateDetailBillingStatus({ ids: workSummaryDetailIds, workOrderId, isBillable });
		await invalidateWorkSummaryStatus();
	}, [bulkUpdateDetailBillingStatus, workOrderId, invalidateWorkSummaryStatus]);

	const onBillingCodeSelect = React.useCallback(async (
		billingCode: Nullable<BillingCodeVM>,
		workSummaryDetailIds: number[]
	) => {
		if (isWorkSummaryReadOnly) {
			return;
		}

		const assignBillingCodeRM: WorkSummaryDetailsAssignBillingCodeRM = {
			billingCodeId: billingCode?.id ?? null,
			fieldReportId,
			workOrderId,
			workSummaryDetailIds,
		};

		await assignBillingCode(assignBillingCodeRM);
		await invalidateWorkSummaryStatus();
	}, [assignBillingCode, fieldReportId, invalidateWorkSummaryStatus, isWorkSummaryReadOnly, workOrderId]);

	const alternativeWorkSummaryDetails = React.useMemo(() => {
		if (!workSummaryDetails) {
			return new Set<WorkSummaryDetailVM>();
		}
		const currHighlightedGroup = highlightedGroup ?? highlightedGroupKey;
		const currHighlightedGroupWsd = workSummaryDetails.find((_wsd) => {
			const _wsdGroup = _wsd.workSummaryGroup ?? resolveHighlightGroupKey(_wsd);
			if (currHighlightedGroup === _wsdGroup) {
				return true;
			}
		});

		if (!currHighlightedGroupWsd) {
			return new Set<WorkSummaryDetailVM>();
		}

		const newAlternativeWorkSummaryDetails = workSummaryDetails.reduce<Set<WorkSummaryDetailVM>>((_acc, _wsd) => {
			if (_wsd.billableWorkId !== currHighlightedGroupWsd.billableWorkId
				&& ArrayUtils.areEqualByKeys(_wsd.definitionFields, currHighlightedGroupWsd.definitionFields, ['value', 'fieldType', 'name'])
				&& _wsd.workType === currHighlightedGroupWsd.workType
				&& _wsd.billingType === currHighlightedGroupWsd.billingType) {
				_acc.add(_wsd);
			}
			return _acc;
		}, new Set());

		return newAlternativeWorkSummaryDetails;
	}, [highlightedGroup, highlightedGroupKey, workSummaryDetails]);

	const alternativeWorkSummary = React.useMemo(() => {
		if (!workSummaries) {
			return new Set<WorkSummaryVM>();
		}

		const currHighlightedGroup = highlightedGroup ?? highlightedGroupKey;

		const currHighlightedGroupWs = workSummaries?.find((_wsd) => {
			const _wsdGroup = _wsd.workSummaryGroup ?? resolveHighlightGroupKey({
				billingType: _wsd.billingType,
				subJobId: _wsd.subJobId,
				billableWorkId: _wsd.billableWorkId,
				workType: _wsd.type,
				definitionFields: _wsd.definitionFields,
			});
			if (currHighlightedGroup === _wsdGroup) {
				return true;
			}
		});

		if (!currHighlightedGroupWs) {
			return new Set<WorkSummaryVM>();
		}

		const newAlternativeWorkSummary = workSummaries.reduce<Set<WorkSummaryVM>>((_acc, _ws) => {
			if (_ws.billableWorkId !== currHighlightedGroupWs.billableWorkId
				&& ArrayUtils.areEqualByKeys(_ws.definitionFields, currHighlightedGroupWs.definitionFields, ['value', 'fieldType', 'fieldName'])
				&& _ws.type === currHighlightedGroupWs.type
				&& _ws.billingType === currHighlightedGroupWs.billingType) {
				_acc.add(_ws);
			}
			return _acc;
		}, new Set());

		return newAlternativeWorkSummary;
	}, [highlightedGroup, highlightedGroupKey, workSummaries]);

	const onRowHighlight = React.useCallback((groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => {
		setHighlightedGroup(groupToHighlight);
		setHighlightedGroupKey(groupKeyToHighlight);
	}, []);

	const onWorkSummaryStatusChange = React.useCallback((wss: WorkSummaryStatusEnum) => {
		return async () => {
			if (isWorkSummaryReadOnly && wss !== WorkSummaryStatusEnum.OUTDATED) {
				return;
			}

			await editWorkSummaryStatus(fieldReportId, wss);
			await invalidateWorkSummaryStatus();
		};
	}, [editWorkSummaryStatus, fieldReportId, invalidateWorkSummaryStatus, isWorkSummaryReadOnly]);

	React.useEffect(() => {
		fetchAndInitializeData();
	}, [fetchAndInitializeData]);

	const { billableMaterialCosts, billableWorkSummaries, nonBillableMaterialCosts, nonBillableWorkSummaries } = React.useMemo<WorkSummariesPartitioned>(() => {
		if (!workSummaries) {
			return { billableWorkSummaries: null, nonBillableWorkSummaries: null, billableMaterialCosts: null, nonBillableMaterialCosts: null };
		}
		return workSummaries.reduce<WorkSummariesPartitioned>((_acc, _curr) => {
			if (_curr.isBillable) {
				if (_curr.billingType === WorkSummaryBillingType.BILLABLE_WORK) {
					_acc.billableWorkSummaries?.push(_curr);
				} else if (_curr.billingType === WorkSummaryBillingType.MATERIAL_COST) {
					_acc.billableMaterialCosts?.push(_curr);
				}
			} else {
				if (_curr.billingType === WorkSummaryBillingType.BILLABLE_WORK) {
					_acc.nonBillableWorkSummaries?.push(_curr);
				} else if (_curr.billingType === WorkSummaryBillingType.MATERIAL_COST) {
					_acc.nonBillableMaterialCosts?.push(_curr);
				}
			}
			return _acc;
		}, { billableWorkSummaries: [], nonBillableWorkSummaries: [], billableMaterialCosts: [], nonBillableMaterialCosts: [] });
	}, [workSummaries]);

	const handleReallocateToSubJob = React.useCallback(async (subJob: SiblingSubjobVM, workSummaryDetailIds: number[]) => {
		if (isWorkSummaryReadOnly) {
			return;
		}
		await reallocateToSubJob({ fieldReportId, workOrderId, subJobId: subJob.id, workSummaryDetailIds });
		await invalidateWorkSummaryStatus();
	}, [fieldReportId, invalidateWorkSummaryStatus, isWorkSummaryReadOnly, reallocateToSubJob, workOrderId]);

	const handleBillingCodeUpdate = React.useCallback(async (data: WorkSummaryUpdatedBillingCodesVM, { }: { socketId: string; }) => {
		if (fieldReportId !== data.fieldReportId) {
			return;
		}
		const updatedBillingCodes = await findBillingCodesForJobId(jobId);
		setBillingCodesByJob(ArrayUtils.groupBy(updatedBillingCodes, (_bc) => _bc.workRequestId));

		const fetchedWorkSummaries = await findWorkSummary(fieldReportId);
		setWorkSummaries(fetchedWorkSummaries.sort(_sortWorkSummaries));
	}, [fieldReportId, findBillingCodesForJobId, findWorkSummary, jobId]);

	const handleReallocatedToSubJob = React.useCallback(async (data: WorkSummaryUpdatedBillingCodesVM, { socketId }: { socketId: string; }) => {
		if (socketId === socket.getConnection()?.getId() || fieldReportId !== data.fieldReportId) {
			return;
		}
		const fetchedWorkSummaries = await findWorkSummary(fieldReportId);
		setWorkSummaries(fetchedWorkSummaries.sort(_sortWorkSummaries));
	}, [fieldReportId, findWorkSummary]);

	const reallocateAllBillable = React.useCallback(async (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => {
		if (isWorkSummaryReadOnly) {
			return;
		}
		await reallocateAllBillableToSubJob({ billingType, fieldReportId, workOrderId, subJobId: subJob.id });
		await invalidateWorkSummaryStatus();
	}, [fieldReportId, invalidateWorkSummaryStatus, isWorkSummaryReadOnly, reallocateAllBillableToSubJob, workOrderId]);

	const reallocateAllNonBillable = React.useCallback(async (billingType: WorkSummaryBillingType, subJob: SiblingSubjobVM) => {
		if (isWorkSummaryReadOnly) {
			return;
		}
		await reallocateAllNonBillableToSubJob({ billingType, fieldReportId, workOrderId, subJobId: subJob.id });
		await invalidateWorkSummaryStatus();
	}, [fieldReportId, invalidateWorkSummaryStatus, isWorkSummaryReadOnly, reallocateAllNonBillableToSubJob, workOrderId]);

	React.useEffect(() => {
		socket.connection?.subscribe(SocketEvent.V2.BE.FIELD_REPORT_LIST.WORK_SUMMARY_UPDATE_BILLING_CODES, handleBillingCodeUpdate);
		socket.connection?.subscribe(SocketEvent.V2.BE.FIELD_REPORT_LIST.WORK_SUMMARY_REALLOCATED_TO_SUB_JOB, handleReallocatedToSubJob);

		return () => {
			socket.connection?.unsubscribe(SocketEvent.V2.BE.FIELD_REPORT_LIST.WORK_SUMMARY_UPDATE_BILLING_CODES);
			socket.connection?.unsubscribe(SocketEvent.V2.BE.FIELD_REPORT_LIST.WORK_SUMMARY_REALLOCATED_TO_SUB_JOB);
		};
	}, [handleBillingCodeUpdate, handleReallocatedToSubJob]);

	const toggleMaterialCostSourceFilter = React.useCallback((source: MaterialCostSource) => {
		setMaterialCostSourceFilters((current) => {
			const next = { ...current, [source]: !current[source] };
			if (Object.values(next).every((_check) => !_check)) {
				return MATERIAL_COST_SOURCE_FILTERS_INITIAL_VALUE;
			}
			return next;
		});
	}, []);

	const materialCostSourceFilterContextValue = React.useMemo<IMaterialCostSourceFilterContext>(() => {
		return {
			filters: materialCostSourceFilters,
			toggle: toggleMaterialCostSourceFilter,
		};
	}, [toggleMaterialCostSourceFilter, materialCostSourceFilters]);

	if (
		!billingCodesByJob
		|| !workSummaryDetails
		|| !billableWorkSummaries
		|| !nonBillableWorkSummaries
		|| !billableMaterialCosts
		|| !nonBillableMaterialCosts
		|| !workSummaryDetailsByGroupKey
	) {
		return null;
	}

	if (!workSummaryDetails.length && noWorkSummaryMessage) {
		return (
			<div className={bemBlock('field-report__work-summary', { empty: true })}>
				{noWorkSummaryMessage}
			</div>
		);
	}

	const areAllWorkSummariesBilled = [...billableWorkSummaries, ...billableMaterialCosts].every(_isEveryWorkSummaryBilled);

	const showMaterialCostTables = (!!billableMaterialCosts.length || !!nonBillableMaterialCosts.length);

	const showProductionData = isAllowedToViewProdData && !!productionData;

	return (
		<MaterialCostSourceFilterContext.Provider value={materialCostSourceFilterContextValue}>
			<div className="field-report__work-summary">
				{
					areFRsReadOnly && isAbleToReject &&
					<div className="field-report-type__status-information">
						In order to edit Report Types, please reject Field Report, which will bring it back to edit mode.
					</div>
				}
				{workOrderIsInProgess ? (
					<div className="field-report-type__status-information">
						Work Summary will be generated after Submitting for Review.
					</div>
				) : (
					<div className="field-report-type__status-information">
						This information is based on true, original data that was gathered in the field report.
						Some overrides might have been applied on job level.
						{
							hasPermissionToManageWS &&
							<Link
								className="m-l-s"
								rel="noopener noreferrer"
								target="_blank"
								to={CLIENT.COMPANY.JOBS.PREVIEW_TAB(organizationData.alias, companyData.name, jobId.toString(), 'workSummary')}
							>
								See here
							</Link>
						}
					</div>
				)}
				{!workOrderIsInProgess && (
					<>
						{showProductionData && <ProductionData productionData={productionData} />}
						<WorkSummaryStatus status={workSummaryStatus} />
						<WorkSummaryBillableWork
							alternativeWorkSummary={alternativeWorkSummary}
							areAllWorkSummariesBilled={areAllWorkSummariesBilled}
							areFRsReadOnly={areFRsReadOnly}
							areMaterialsEnabled={areMaterialsEnabled}
							belongsToProject={belongsToProject}
							billingCodes={billingCodesByJob}
							canCompleteWorkSummary={canCompleteWorkSummary}
							fieldReportId={fieldReportId}
							handleJobReallocation={handleReallocateToSubJob}
							highlightedGroup={highlightedGroup}
							highlightedGroupKey={highlightedGroupKey}
							isWorkSummaryStatusCompleted={workSummaryStatus.status === WorkSummaryStatusEnum.COMPLETED}
							isWorkSummaryStatusReviewed={workSummaryStatus.status === WorkSummaryStatusEnum.REVIEWED}
							onBillingCodeSelect={onBillingCodeSelect}
							onBulkEditDetailBillableStatus={onBulkUpdateDetailStatus}
							onReallocateAll={reallocateAllBillable}
							onRowHighlight={onRowHighlight}
							onWorkSummaryStatusChange={onWorkSummaryStatusChange}
							subJobs={subJobs}
							workOrderCode={workOrderCode}
							workOrderId={workOrderId}
							workSummary={billableWorkSummaries}
							workSummaryDetails={workSummaryDetails}
							workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
						/>
						<NonBillableWork
							alternativeWorkSummary={alternativeWorkSummary}
							areFRsReadOnly={areFRsReadOnly}
							belongsToProject={belongsToProject}
							billingCodes={billingCodesByJob}
							fieldReportId={fieldReportId}
							handleJobReallocation={handleReallocateToSubJob}
							highlightedGroup={highlightedGroup}
							highlightedGroupKey={highlightedGroupKey}
							isWorkSummaryStatusCompleted={workSummaryStatus.status === WorkSummaryStatusEnum.COMPLETED}
							isWorkSummaryStatusReviewed={workSummaryStatus.status === WorkSummaryStatusEnum.REVIEWED}
							onBulkEditDetailBillableStatus={onBulkUpdateDetailStatus}
							onReallocateAll={reallocateAllNonBillable}
							onRowHighlight={onRowHighlight}
							subJobs={subJobs}
							workOrderCode={workOrderCode}
							workOrderId={workOrderId}
							workSummary={nonBillableWorkSummaries}
							workSummaryDetails={workSummaryDetails}
							workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
						/>
						{!DisabledFeatures.Materials && areMaterialsEnabled && showMaterialCostTables && (
							<>
								<WorkSummaryMaterialCost
									alternativeWorkSummary={alternativeWorkSummary}
									areFRsReadOnly={areFRsReadOnly}
									belongsToProject={belongsToProject}
									billingCodes={billingCodesByJob}
									fieldReportId={fieldReportId}
									handleJobReallocation={handleReallocateToSubJob}
									highlightedGroup={highlightedGroup}
									highlightedGroupKey={highlightedGroupKey}
									isWorkSummaryStatusCompleted={workSummaryStatus.status === WorkSummaryStatusEnum.COMPLETED}
									isWorkSummaryStatusReviewed={workSummaryStatus.status === WorkSummaryStatusEnum.REVIEWED}
									onBulkEditDetailBillableStatus={onBulkUpdateDetailStatus}
									onReallocateAll={reallocateAllBillable}
									onRowHighlight={onRowHighlight}
									subJobs={subJobs}
									workOrderCode={workOrderCode}
									workOrderId={workOrderId}
									workSummary={billableMaterialCosts}
									workSummaryDetails={workSummaryDetails}
									workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
								/>
								<MaterialNonCost
									alternativeWorkSummary={alternativeWorkSummary}
									areFRsReadOnly={areFRsReadOnly}
									belongsToProject={belongsToProject}
									billingCodes={billingCodesByJob}
									fieldReportId={fieldReportId}
									handleJobReallocation={handleReallocateToSubJob}
									highlightedGroup={highlightedGroup}
									highlightedGroupKey={highlightedGroupKey}
									isWorkSummaryStatusCompleted={workSummaryStatus.status === WorkSummaryStatusEnum.COMPLETED}
									isWorkSummaryStatusReviewed={workSummaryStatus.status === WorkSummaryStatusEnum.REVIEWED}
									onBulkEditDetailBillableStatus={onBulkUpdateDetailStatus}
									onReallocateAll={reallocateAllNonBillable}
									onRowHighlight={onRowHighlight}
									subJobs={subJobs}
									workOrderCode={workOrderCode}
									workOrderId={workOrderId}
									workSummary={nonBillableMaterialCosts}
									workSummaryDetails={workSummaryDetails}
									workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
								/>
							</>
						)}
					</>
				)}
				<WorkSummaryDetails
					allowToMarkAllAsNonBillable={allowToMarkAllAsNonBillable && !isWorkSummaryReadOnly}
					alternativeWorkSummaryDetails={alternativeWorkSummaryDetails}
					canResetJobWorkSummary={isPlatformAdmin}
					fieldReportId={fieldReportId}
					highlightedGroup={highlightedGroup}
					highlightedGroupKey={highlightedGroupKey}
					isReadOnly={isWorkSummaryReadOnly}
					onBillingStatusToggle={onBillingStatusToggle}
					onBulkStatusUpdate={handleBulkUpdateBillableStatusForFieldReport}
					onRowHighlight={onRowHighlight}
					pathName={pathName}
					resetJobWorkSummary={resetJobWorkSummary}
					timeZone={timeZone}
					workOrderCode={workOrderCode}
					workSummaryDetails={workSummaryDetails}
					workSummaryDetailsByGroupKey={workSummaryDetailsByGroupKey}
				/>
			</div>
		</MaterialCostSourceFilterContext.Provider>
	);
};

const mapStateToProps = (state: RootState) => {
	const { companyData, organizationData, userData } = state.user;
	const { company } = state.company;
	if (!companyData || !organizationData || !userData) {
		throw new Error('User not logged in');
	}

	const { isCompanyAdmin, permissions } = companyData;
	const { role } = userData;

	const isAllowedToViewProdData = isAllowed(PagePermissions.COMPANY.PROD_DATA.VIEW, permissions, isCompanyAdmin, role);

	return {
		companyData,
		isAllowedToViewProdData,
		organizationData,
		allowToMarkAllAsNonBillable: company?.allowAllNonBillableShortcut ?? false,
		isPlatformAdmin: UserUtils.isAdmin(userData),
		areMaterialsEnabled: companyData.areMaterialsEnabled,
	};
};

function mapDispatchToProps() {
	return {
		findBillingCodesForJobId: BillingCodeActions.findAllForProjectByJobId,
		findWorkSummaryDetails: FieldReportActions.findWorkSummaryDetails,
		findWorkSummary: FieldReportActions.findWorkSummary,
		findProductionData: WorkOrderActions.findProductionData,
		updateBillingStatuses: WorkSummaryDetailsActions.updateBillingStatuses,
		bulkUpdateBillableStatusForFieldReport: WorkSummaryDetailsActions.bulkUpdateBillableStatusForFieldReport,
		bulkUpdateDetailBillingStatus: WorkSummaryDetailsActions.bulkUpdateDetailBillingStatus,
		assignBillingCode: WorkSummaryDetailsActions.assignBillingCode,
		editWorkSummaryStatus: WorkOrderActions.editWorkSummaryStatus,
		checkIfBillableWorkExists: FieldReportActions.checkIfBillableWorkExists,
		resetJobWorkSummary: FieldReportActions.resetJobWorkSummary,
		findSiblingSubjobs: WorkRequestActions.findSiblingSubjobs,
		reallocateToSubJob: WorkSummaryDetailsActions.reallocateToSubJob,
		reallocateAllBillableToSubJob: WorkSummaryDetailsActions.reallocateAllBillableToSubJob,
		reallocateAllNonBillableToSubJob: WorkSummaryDetailsActions.reallocateAllNonBillableToSubJob,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(WorkSummary);
